import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar, ArrowRight, Flame } from "lucide-react";
import { Link } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Skeleton } from "@/components/ui/skeleton";
import type { HabitWithStreakState, HabitEntry } from "@shared/schema";
import { formatDateInput, getTodayDateString, parseLocalDate } from "@/lib/dateUtils";
import { utcEntryDay } from "@/lib/trackerx-dates";

/**
 * Current consecutive-day streak ending today, computed on calendar-day keys.
 *
 * Entries are stored at UTC midnight of the user's picked LOCAL day, so the
 * day of an entry is its UTC date component (utcEntryDay) — never a
 * local-time conversion of the stored instant, which would count each entry
 * as the previous day for users west of UTC. "Today" and the expected
 * preceding days are local calendar days (formatDateInput).
 */
export function calculateDashboardStreak(entries: HabitEntry[], todayKey: string): number {
  const completedDays = new Set(
    entries.filter(e => e.completed && e.date).map(e => utcEntryDay(e.date!)),
  );
  let streak = 0;
  const cursor = parseLocalDate(todayKey);
  while (completedDays.has(formatDateInput(cursor))) {
    streak++;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

function HabitRow({ habit, today }: { habit: HabitWithStreakState; today: string }) {
  const { data: habitEntries = [], isLoading: entriesLoading } = useQuery<HabitEntry[]>({
    queryKey: [`/api/habits/${habit.id}/entries`, "monthly", today],
    queryFn: async () => {
      const response = await fetch(`/api/habits/${habit.id}/entries?period=monthly`);
      if (!response.ok) throw new Error('Failed to fetch habit entries');
      return response.json();
    },
  });

  // Check if completed today using local date for "today" and UTC-date extraction for
  // stored entries (server stores entries as UTC midnight of the local check-in date, so
  // the UTC date portion equals the intended local date).
  const todayEntry = habitEntries.find(
    entry => entry.date && utcEntryDay(entry.date) === today
  );
  const completedToday = todayEntry?.completed || false;

  // Current streak on calendar-day keys (see calculateDashboardStreak).
  const currentStreak = calculateDashboardStreak(habitEntries, today);

  // Calculate progress percentage based on target and period
  const getProgressPercentage = () => {
    const completedCount = habitEntries.filter(e => e.completed).length;
    if (habit.target === 0) return 0;
    return Math.min(Math.round((completedCount / habit.target) * 100), 100);
  };

  const progressPercentage = getProgressPercentage();

  // Toggle completion for today
  const toggleCompleteMutation = useMutation({
    mutationFn: async (completed: boolean) => {
      return apiRequest("POST", `/api/habits/${habit.id}/entries`, {
        date: formatDateInput(new Date()),
        completed,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/habits/${habit.id}/entries`] });
      queryClient.invalidateQueries({ queryKey: ["/api/habits"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/habits"] });
    },
    onError: () => {
    },
  });

  const handleToggle = () => {
    toggleCompleteMutation.mutate(!completedToday);
  };

  if (entriesLoading) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg border border-border">
        <Skeleton className="h-5 w-5" />
        <div className="flex-1">
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-2 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
      data-testid={`row-habit-${habit.id}`}
    >
      <Checkbox
        checked={completedToday}
        onCheckedChange={handleToggle}
        disabled={toggleCompleteMutation.isPending}
        className="mt-0.5"
        data-testid={`button-complete-habit-${habit.id}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h4
            className={`font-medium text-sm ${
              completedToday ? "line-through text-muted-foreground" : "text-foreground"
            }`}
            data-testid={`text-habit-title-${habit.id}`}
          >
            {habit.name}
          </h4>
          {(() => {
            const streak = habit.currentStreak ?? currentStreak;
            const checkedInToday = habit.checkedInToday ?? completedToday;
            const dueToday = habit.dueToday ?? (streak > 0 && !completedToday);
            const badgeState =
              streak > 0 && checkedInToday ? "done" :
              streak > 0 && dueToday ? "pending" :
              "broken";
            if (badgeState === "broken") return null;
            return (
              <div className={`flex items-center gap-1 text-xs ${badgeState === "pending" ? "text-amber-500" : "text-orange-500"}`}>
                <Flame className="h-3 w-3" />
                <span>{streak} days{badgeState === "pending" ? " ●" : ""}</span>
              </div>
            );
          })()}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            Target: {habit.target} ({habit.frequency})
          </span>
          <Progress value={progressPercentage} className="h-1.5 flex-1" />
          <span className="text-xs text-muted-foreground w-10 text-right">
            {progressPercentage}%
          </span>
        </div>
      </div>
    </div>
  );
}

export function HabitsDashboardWidget() {
  // Track today's date in state so that a midnight flip detected on tab focus
  // drives a new query key and forces a fresh fetch automatically.
  const [localDate, setLocalDate] = useState(() => getTodayDateString());

  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        const freshDate = getTodayDateString();
        setLocalDate((prev) => {
          if (prev !== freshDate) {
            // Optimistic streak guard: immediately flip checkedInToday → false
            // and dueToday → true for any habit with an active streak so the
            // badge switches from orange ("done") to amber ("pending") before
            // the network round-trip completes. Mirrors the mobile
            // implementation in onelifemap-mobile/app/(tabs)/habits.tsx.
            queryClient.setQueryData<HabitWithStreakState[]>(
              ["/api/habits"],
              (old) => {
                if (!old) return old;
                return old.map((h) => {
                  const streak = h.currentStreak ?? 0;
                  if (streak > 0 && h.checkedInToday) {
                    return { ...h, checkedInToday: false, dueToday: true };
                  }
                  return h;
                });
              }
            );
            // Date rolled over — invalidate all habit-related queries so every
            // habit-row entry query also re-fetches with the new day's data.
            queryClient.invalidateQueries();
            return freshDate;
          }
          return prev;
        });
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  const { data: habits = [], isLoading } = useQuery<HabitWithStreakState[]>({
    queryKey: ["/api/habits"],
  });

  // Filter active habits and limit to 6
  const activeHabits = habits.filter(h => h.isActive).slice(0, 6);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Today's Habits
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Today's Habits
          </CardTitle>
          {activeHabits.length > 0 && (
            <Link href="/habits">
              <Button
                variant="ghost"
                size="sm"
                className="text-primary hover:text-primary/90"
                data-testid="link-view-all-habits"
              >
                View All
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {activeHabits.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
            <p className="text-sm">
              No active habits. Create habits to track your daily routines.
            </p>
            <Link href="/habits">
              <Button variant="outline" size="sm" className="mt-4">
                Go to Habits
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            {activeHabits.map((habit) => (
              <HabitRow key={habit.id} habit={habit} today={localDate} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
